import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkle, Camera, Sun, Crop, Lightning, House, Handshake, Broom, ChartLine } from "@phosphor-icons/react";
import ProntoLogo from "@/components/ProntoLogo";

const POSTS = {
  "mandati-esclusiva-tecniche": {
    title: "Acquisire mandati in esclusiva: 5 tecniche che funzionano davvero nel 2026",
    description:
      "Lo script di apertura, la valutazione che convince, il piano marketing che fa la differenza. Tecniche testate sul campo per chiudere l'esclusiva al primo appuntamento.",
    date: "8 maggio 2026",
    readingTime: "9 min",
    tag: "Acquisizione",
    body: <MandatiEsclusiva />,
  },
  "home-staging-low-cost": {
    title: "Home staging low cost: 7 cose da fare nelle 2 ore prima di una visita",
    description:
      "Senza spendere un euro in mobili. Trucchi pratici di pulizia, profumazione e ordine che alzano del 5–10% le offerte ricevute.",
    date: "7 maggio 2026",
    readingTime: "7 min",
    tag: "Visite",
    body: <HomeStagingLowCost />,
  },
  "idealista-vs-immobiliare-it": {
    title: "Idealista vs Immobiliare.it: dove pubblicare per primo e come ottimizzare il ranking",
    description:
      "I due portali pesano in modo diverso ogni elemento dell'annuncio. La sequenza giusta di pubblicazione fa risparmiare giorni di visibilità.",
    date: "6 maggio 2026",
    readingTime: "8 min",
    tag: "Portali",
    body: <IdealistaVsImmobiliare />,
  },
  "foto-immobili-smartphone": {
    title: "Come fare foto immobili professionali con lo smartphone (senza essere fotografo)",
    description:
      "I 7 trucchi pratici per scattare foto da listing-grade con un cellulare normale. Esposizione, prospettiva, ordine, dettagli. Più come Pronto le rifinisce in pochi secondi.",
    date: "5 maggio 2026",
    readingTime: "6 min",
    tag: "Guida pratica",
    body: <FotoImmobiliSmartphone />,
  },
  "annuncio-immobiliare-vendita": {
    title: "Come scrivere un annuncio immobiliare che vende davvero (esempi inclusi)",
    description:
      "Titolo, descrizione, parole chiave, errori da evitare. Una guida pratica con esempi reali per agenti italiani.",
    date: "3 maggio 2026",
    readingTime: "8 min",
    tag: "Marketing",
    body: <AnnuncioVendita />,
  },
  "valutazione-immobile-veloce": {
    title: "Come fare una valutazione immobiliare veloce al cliente (in 5 minuti)",
    description:
      "OMI, comparazione di mercato, e i 3 numeri che ogni agente deve avere a portata di mano per non perdere un'acquisizione.",
    date: "1 maggio 2026",
    readingTime: "5 min",
    tag: "Acquisizione",
    body: <ValutazioneVeloce />,
  },
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS[slug];

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} · Pronto`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = post.description;
    return () => { document.title = "Pronto"; };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="border-b border-[#EDEAE2] bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="blog-post-home">
            <ProntoLogo size={28} />
          </Link>
          <Link to="/register" className="btn-primary text-[13px]" data-testid="blog-post-register">
            Provala gratuitamente <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 lg:px-10 py-8 sm:py-12">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold mb-6 hover:underline" data-testid="blog-post-back">
          <ArrowLeft size={12} weight="bold" /> Torna al blog
        </Link>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="inline-block px-2 py-0.5 rounded-full bg-[#F1F4ED] text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5A6A4D]">{post.tag}</span>
            <span className="text-[11px] text-[#9A968B]">{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-[#E5E7E0]" />
            <span className="text-[11px] text-[#9A968B]">{post.readingTime} di lettura</span>
          </div>
          <h1 className="font-editorial text-[28px] sm:text-4xl leading-[1.15] tracking-tight text-[#1C1C1A]">
            {post.title}
          </h1>
        </div>

        <article className="prose-pronto" data-testid="blog-post-body">
          {post.body}
        </article>

        <div className="mt-12 bg-white border border-[#E5E7E0] rounded-xl p-6 sm:p-8 text-center">
          <Sparkle size={24} weight="fill" className="mx-auto text-amber-600 mb-3" />
          <h3 className="font-editorial text-[20px] sm:text-2xl text-[#1C1C1A] mb-2 leading-tight">
            Ti è stato utile?
          </h3>
          <p className="text-[14px] text-[#6B6A65] mb-5 max-w-md mx-auto">
            Pronto fa tutto questo per te. Foto migliorate in pochi secondi, annunci scritti
            automaticamente, valutazioni veloci. Gratis per 30 giorni.
          </p>
          <Link to="/register" className="btn-primary inline-flex" data-testid="blog-post-bottom-cta">
            Iscrizione gratuita <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-[#EDEAE2] bg-white py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center text-[12px] text-[#9A968B]">
          <Link to="/blog" className="hover:text-[#1C1C1A] transition-colors mr-3">Tutti gli articoli</Link>
          <span className="text-[#E5E7E0]">·</span>
          <Link to="/" className="hover:text-[#1C1C1A] transition-colors ml-3">Pronto</Link>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST 1 — Foto immobili con smartphone
// ─────────────────────────────────────────────────────────────────────────────
function FotoImmobiliSmartphone() {
  return (
    <>
      <p className="lead">
        Il primo scroll di un cliente su Idealista dura <strong>1,7 secondi</strong>. Se la foto
        è scura, storta o disordinata, passa oltre. Non importa quanto sia bella la casa: con
        una foto fatta male, l'annuncio è invisibile.
      </p>
      <p>
        La buona notizia: non serve una reflex né un fotografo. Uno smartphone moderno fa foto
        eccellenti se sai 7 trucchi pratici. Te li spiego in ordine, dal più importante al meno.
      </p>

      <h2><Camera size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />1. Spegni il flash. Sempre.</h2>
      <p>
        Il flash dello smartphone fa schifo per gli interni: appiattisce, crea ombre dure,
        rende i muri verdognoli. Tienilo spento e usa la luce naturale che entra dalle finestre.
        Se la stanza è buia, accendi tutte le luci della casa — abat-jour, faretti, plafoniera —
        e fai entrare più luce possibile dalle persiane.
      </p>

      <h2><Sun size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />2. Scatta nelle ore giuste</h2>
      <p>
        La luce migliore è la mattina (10-12) o il pomeriggio (15-17), con cielo coperto.
        Il sole pieno crea contrasti troppo forti che lo smartphone non gestisce. Se la casa
        è esposta a sud, scatta presto. Se a nord, scatta a metà giornata. Evita golden hour
        (tramonto): troppa luce arancione, sembra finto.
      </p>

      <h2><Crop size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />3. Tieni il telefono dritto e basso</h2>
      <p>
        Il classico errore: telefono inclinato verso l'alto = muri storti a trapezio. Per
        evitarlo:
      </p>
      <ul>
        <li>Tieni il telefono <strong>orizzontale</strong> (paesaggio), non in verticale.</li>
        <li>Posizionalo all'altezza del torace (≈120 cm da terra). Mai sopra la testa, mai sotto le ginocchia.</li>
        <li>Attiva la <strong>griglia</strong> nelle impostazioni della fotocamera: ti aiuta a tenere le linee parallele.</li>
        <li>Inquadra dall'angolo della stanza, mai dal centro: cattura più spazio.</li>
      </ul>

      <h2><Lightning size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />4. Tocca lo schermo per mettere a fuoco</h2>
      <p>
        Lo smartphone mette a fuoco automaticamente, ma spesso sbaglia. <strong>Tocca un punto
        ben illuminato della stanza</strong> (non la finestra, non un'ombra): il telefono regola
        esposizione e fuoco su quel punto. Se la finestra è bruciata, tocca proprio lì un attimo
        per far ridurre l'esposizione, poi sposta il fuoco.
      </p>

      <h2><House size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />5. Riordina prima di scattare</h2>
      <p>
        Foto da agente esperto vs foto da principiante: la differenza è tutta qui. Prima di
        scattare:
      </p>
      <ul>
        <li><strong>Cucina</strong>: sgombera il piano cottura, togli stracci e detersivi, raddrizza le sedie.</li>
        <li><strong>Bagno</strong>: nascondi spazzolini, asciugamani usati, prodotti. Lascia un asciugamano pulito piegato.</li>
        <li><strong>Camera</strong>: rifai il letto con cura, gonfia i cuscini, togli oggetti dal comodino.</li>
        <li><strong>Salone</strong>: aspira le briciole, sistema cuscini sul divano, togli telecomandi.</li>
        <li><strong>Sempre</strong>: nascondi cestini, scarpe, peluche, fili dei caricabatterie.</li>
      </ul>
      <p>
        Sembra ovvio, ma il 70% degli annunci su Idealista ha foto con un cestino in vista.
        Sei già davanti al 70% solo per questo.
      </p>

      <h2><Camera size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />6. Scatta tante foto della stessa stanza</h2>
      <p>
        Dalla stessa stanza fai 4-5 scatti da angoli diversi: angolo nord-ovest, nord-est,
        sud-ovest, sud-est, e uno verso la finestra principale. Sceglierai dopo. Costa zero
        e ti permette di pubblicare quella migliore. Mai accontentarti del primo scatto.
      </p>

      <h2><Sparkle size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />7. Rifinisci con uno strumento giusto</h2>
      <p>
        Le foto da smartphone, anche fatte bene, escono sempre un po' opache, leggermente
        storte, con luce non perfettamente bilanciata. Qui entra il post-processing — ed è qui
        che fai il salto da "agente che pubblica foto" a "agente che pubblica annunci da
        rivista".
      </p>
      <p>
        Strumenti come <strong>Pronto</strong> sistemano automaticamente:
      </p>
      <ul>
        <li>Linee verticali raddrizzate (niente più muri storti)</li>
        <li>Esposizione bilanciata (niente più zone scure o bruciate)</li>
        <li>Colori puliti, bianchi neutri (no virata gialla/blu)</li>
        <li>Riordino digitale dei dettagli rimasti fuori posto</li>
      </ul>
      <p>
        Tutto in pochi secondi, da telefono, senza dover imparare Photoshop o Lightroom. Quello
        che un fotografo professionista fa in un'ora di post-produzione. Quindi la differenza
        non è più "fotografo vs te" — è "agente con un buon tool vs agente senza".
      </p>

      <h3>In sintesi</h3>
      <p>
        Foto immobiliari professionali con lo smartphone si fanno in 4 mosse: <strong>luce
        naturale, telefono dritto, riordino, post-processing</strong>. Se rispetti queste 4 cose
        (e basta uno smartphone degli ultimi 5 anni) il tuo annuncio si distingue immediatamente
        dal resto della pagina.
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST 2 — Annuncio immobiliare che vende
// ─────────────────────────────────────────────────────────────────────────────
function AnnuncioVendita() {
  return (
    <>
      <p className="lead">
        Sui portali, l'utente prima guarda la foto, poi il prezzo, poi il titolo. Se il
        titolo non lo aggancia in 3 secondi, scrolla. La descrizione la legge solo l'1% degli
        utenti — ma sono il 1% che chiama. Quindi sì, anche le parole contano.
      </p>

      <h2>Il titolo: 50 caratteri, 3 informazioni</h2>
      <p>
        Un buon titolo immobiliare ha esattamente 3 cose: <strong>tipologia + zona + un
        elemento distintivo</strong>. Niente di più.
      </p>
      <p>
        ❌ "Bellissimo appartamento luminoso e ben rifinito"<br />
        ❌ "Occasione unica! Affare imperdibile"<br />
        ✅ "Trilocale ristrutturato, terrazzo, Centro storico"<br />
        ✅ "Bilocale con vista, ascensore, vicino metro"
      </p>
      <p>
        Niente aggettivi vuoti ("bellissimo", "particolare", "unico"). Niente urgenza falsa
        ("affare", "occasione"). Solo fatti. L'aggettivo specifico (ristrutturato, con terrazzo,
        ascensore) vale più di 10 superlativi.
      </p>

      <h2>La descrizione: 4 paragrafi, in quest'ordine</h2>
      <ol>
        <li><strong>Hook iniziale</strong> (1 frase): il punto di forza più rilevante. Es. "A 200 metri dalla metro Lanza, in palazzo storico ristrutturato."</li>
        <li><strong>Configurazione interna</strong>: numero stanze, metratura, esposizione, piano. Es. "L'immobile si compone di salone con cucina abitabile a vista, due camere matrimoniali, bagno e ripostiglio. Esposizione est/ovest, terzo piano con ascensore."</li>
        <li><strong>Stato e finiture</strong>: cosa è stato rifatto, anno costruzione, classe energetica. Es. "Ristrutturazione 2023: nuovi impianti, parquet di rovere, infissi a doppio vetro. Classe B."</li>
        <li><strong>Servizi vicini e CTA</strong>: cosa c'è intorno + invito al contatto. Es. "A pochi passi da supermercato, scuole, parchi e fermata MM. Visita su appuntamento."</li>
      </ol>

      <h2>Parole chiave che funzionano (e quelle che NON funzionano)</h2>
      <p>
        I portali hanno motori di ricerca interni: usare le parole giuste = più visibilità
        organica. Le parole che funzionano davvero sui portali italiani:
      </p>
      <ul>
        <li><strong>Stato</strong>: ristrutturato, da ristrutturare, abitabile, ottime condizioni</li>
        <li><strong>Esposizione</strong>: doppia esposizione, luminoso, esposizione sud</li>
        <li><strong>Caratteristiche</strong>: terrazzo, balcone, giardino privato, posto auto, box, cantina, ascensore</li>
        <li><strong>Zona</strong>: centro storico, semicentro, residenziale, vicino metro, vicino scuole</li>
        <li><strong>Classe energetica</strong>: A, A1, A2, A3, A4 (le sotto B fanno scendere il ranking)</li>
      </ul>
      <p>
        Parole inutili che peggiorano il ranking: "occasione", "affare", "trattabile",
        "vedere per credere", "informazioni in agenzia". Sono spam keywords e Idealista
        / Immobiliare.it le penalizzano.
      </p>

      <h2>Errori da evitare assolutamente</h2>
      <ul>
        <li><strong>Tutto maiuscolo</strong>: "TRILOCALE RISTRUTTURATO". Sembra che urli. Penalizzato.</li>
        <li><strong>Punteggiatura eccessiva</strong>: "Bellissimo!!! Da vedere!!!". Anche penalizzato.</li>
        <li><strong>Emoji</strong>: 🏠🌟⭐. I portali italiani li nascondono o filtrano l'annuncio.</li>
        <li><strong>Prezzi tondi falsi</strong>: "€ 250.000 trattabili" → meglio "€ 245.000" e basta. Il "trattabile" comunica al cliente "puoi farmi un'offerta bassa".</li>
        <li><strong>Foto come prima foto della planimetria</strong>: chi vuole vedere una piantina prima delle stanze? Lascia la planimetria all'ultimo posto.</li>
      </ul>

      <h3>In sintesi</h3>
      <p>
        Un annuncio che vende è specifico, ordinato, senza trucchi. Titolo con 3 informazioni
        concrete, descrizione in 4 paragrafi (hook, configurazione, stato, zona), parole chiave
        coerenti coi portali. Niente superlativi, niente urgenza, niente emoji. Sembra noioso,
        ma è esattamente quello che cerca chi sta comprando casa: <strong>fatti veri per una
        decisione vera</strong>.
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST 3 — Valutazione veloce
// ─────────────────────────────────────────────────────────────────────────────
function ValutazioneVeloce() {
  return (
    <>
      <p className="lead">
        L'acquisizione si gioca nei primi 5 minuti. Il proprietario ti chiede "quanto vale?"
        e tu hai 3 risposte possibili: dare un numero a caso (rischi credibilità), dire "ti
        richiamo" (rischi di perderlo), oppure dare un range realistico in 5 minuti. Vince
        chi sa fare la terza.
      </p>

      <h2>I 3 numeri che ti servono sempre</h2>
      <p>
        Per dare una valutazione veloce e credibile ti bastano 3 dati:
      </p>
      <ol>
        <li><strong>OMI €/m² della zona</strong>: il valore medio dell'Agenzia delle Entrate per quella zona omogenea.</li>
        <li><strong>Coefficiente di rivalutazione</strong>: stato dell'immobile (ottimo, buono, da ristrutturare), piano, esposizione, ascensore.</li>
        <li><strong>Comparazione di mercato</strong>: 3 annunci attivi simili nella stessa zona — non quelli venduti, quelli in vendita oggi.</li>
      </ol>

      <h2>Come calcolarlo in 5 minuti</h2>
      <p>
        <strong>Passo 1 (1 min)</strong>: Vai su <em>OMI Agenzia Entrate</em>, inserisci comune
        e zona. Ti restituisce €/m² per ogni tipologia (residenziale ottimo, normale, scadente).
        Tieni il valore "normale".
      </p>
      <p>
        <strong>Passo 2 (1 min)</strong>: Applica i coefficienti:
      </p>
      <ul>
        <li>Stato: ottimo +10/+15%, normale 0%, da ristrutturare -15/-25%</li>
        <li>Piano: 1° -5%, 2°/3° 0%, attico +5/+10%</li>
        <li>Ascensore: -10% se assente in palazzo &gt; 2 piani</li>
        <li>Esposizione: doppia/sud +3%, nord -3%</li>
        <li>Pertinenze: balcone +2%, terrazzo +5%, box auto +€8-15k, cantina +€2-4k</li>
      </ul>
      <p>
        <strong>Passo 3 (3 min)</strong>: Apri Idealista, filtra per stessa tipologia e zona,
        leggi 3 annunci simili attivi. Vedi il prezzo richiesto. Tieni conto che il prezzo
        finale di vendita è in media il 7-12% sotto il richiesto.
      </p>
      <p>
        Ora hai un range. Esempio: OMI €2.800/m² × 90 m² = €252.000 base. Stato buono +5% =
        €264.600. Piano 2° con ascensore 0%. Doppia esposizione +3% = €272.500. Annunci
        simili a €265.000-285.000 → range realistico <strong>€265.000-280.000</strong>.
      </p>

      <h2>Come comunicarlo al proprietario</h2>
      <p>
        Mai un numero secco. Sempre un range. Esempio di copione:
      </p>
      <p>
        <em>"Sulla base dei dati OMI della zona, dello stato dell'immobile e di 3 annunci
        simili attivi al momento, il valore di mercato realistico è tra 265 e 280 mila euro.
        Per posizionarci competitivi consiglio di partire da 285.000, lasciando margine di
        trattativa per chiudere intorno a 270.000-275.000. Se invece vogliamo vendere veloce,
        265.000 è un prezzo che attira richieste in 2 settimane."</em>
      </p>
      <p>
        Hai dato 3 informazioni: il valore reale, la strategia di pricing, il timeline. Sei
        passato da "agente che spara numeri" a <strong>consulente</strong>. Acquisizione fatta.
      </p>

      <h2>Errori da evitare</h2>
      <ul>
        <li><strong>Sovrastimare per acquisire</strong>: il proprietario è felice 2 settimane, poi vede che non chiama nessuno e cambia agenzia. Hai perso tempo e fiducia.</li>
        <li><strong>Sottostimare per chiudere veloce</strong>: il proprietario lo capisce confrontando con altri agenti. Perdi credibilità.</li>
        <li><strong>Dare un numero senza spiegazione</strong>: "Vale 270.000". Perché? Se non sai spiegarlo, sembra inventato.</li>
        <li><strong>Non considerare il tempo di vendita</strong>: vendere in 30 giorni vs in 6 mesi è una scelta di pricing diversa. Va condivisa col cliente.</li>
      </ul>

      <h3>In sintesi</h3>
      <p>
        Una valutazione veloce e credibile si fa in 5 minuti con 3 dati: OMI, coefficienti,
        comparativa mercato. Non serve un perito. Serve un agente che sa dove guardare. Se la
        comunichi come un range con strategia, ti acquisisci il mandato. Se la spari a caso,
        lo perdi.
      </p>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// POST 4 — Mandati in esclusiva
// ─────────────────────────────────────────────────────────────────────────────
function MandatiEsclusiva() {
  return (
    <>
      <p className="lead">
        Il mandato in esclusiva non si vince a parole né con sconti sulla provvigione. Si vince
        dimostrando che tu sai vendere quella casa <strong>meglio di chiunque altro</strong>, e
        che lo fai con un metodo che il proprietario può capire in 5 minuti. Ecco le 5 tecniche
        che chiudono l'esclusiva al primo appuntamento.
      </p>

      <h2><Handshake size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />1. Lo script di apertura: parla del proprietario, non di te</h2>
      <p>
        Il 90% degli agenti entra in casa e attacca con "noi siamo l'agenzia X, abbiamo
        venduto N case, blablabla". Errore. Il proprietario non vuole sapere di te. Vuole capire
        se tu hai capito <em>la sua situazione</em>. Apri così:
      </p>
      <p>
        <em>"Buongiorno Sig. Ferrari. Prima di parlarLe di me, mi racconti Lei: perché vende? In
        quanto tempo Le serve chiudere? E qual è il prezzo che ha in mente?"</em>
      </p>
      <p>
        I primi 3 minuti li passi ad ascoltare. Prendi appunti. Poi rispecchi: "Quindi mi sta
        dicendo che Le serve vendere entro fine anno, a un prezzo intorno ai 280, ed è la prima
        volta che vende casa". Adesso il proprietario sa che hai capito. <strong>Adesso puoi
        parlare.</strong>
      </p>

      <h2><ChartLine size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />2. La valutazione documentata (non un foglio, una storia)</h2>
      <p>
        Arrivi con un report stampato di 4 pagine: pagina 1 = OMI della zona; pagina 2 = 5
        annunci comparabili attivi (con foto + prezzo + giorni online); pagina 3 = 3 case
        vendute negli ultimi 6 mesi e a quanto sono uscite davvero; pagina 4 = il TUO
        posizionamento consigliato con strategia (prezzo iniziale, prezzo trattabile, prezzo
        minimo accettabile).
      </p>
      <p>
        Il proprietario tiene quel report in mano per giorni, lo mostra alla moglie, lo studia.
        Tu hai trasformato "una stima" in un <strong>documento di consulenza</strong>. Nessun
        altro agente fa così.
      </p>

      <h2><Sparkle size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />3. Il piano marketing scritto (3 pagine, non un PDF generico)</h2>
      <p>
        L'esclusiva si vende quando il proprietario capisce che <strong>tu lavori e gli altri
        non lavorano</strong>. Mostragli il piano:
      </p>
      <ul>
        <li><strong>Settimana 1</strong>: shooting fotografico professionale + foto rifinite automaticamente + planimetria 2D arredata</li>
        <li><strong>Settimana 1</strong>: virtual tour 360° online</li>
        <li><strong>Settimana 2</strong>: pubblicazione su Idealista, Immobiliare.it, Casa.it, sito proprio + invio a 30+ contatti pre-qualificati nel CRM</li>
        <li><strong>Settimana 2-3</strong>: campagna social Instagram + reel + Story</li>
        <li><strong>Settimana 3-4</strong>: open house + report visite settimanali al proprietario</li>
        <li><strong>Mese 2</strong>: revisione strategia se non chiudiamo in 30 giorni (ribasso o cambio canali)</li>
      </ul>
      <p>
        Adesso confronta col tuo concorrente che gli dice "la pubblichiamo sui portali e
        vediamo". Chi sembra più professionale?
      </p>

      <h2><House size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />4. La provvigione la spieghi col valore, non col prezzo</h2>
      <p>
        Quando arriva il momento della provvigione (3% + IVA tipico), NON dire "è la nostra
        tariffa". Dì: <em>"Sui 280.000 euro, 8.400 euro è il mio compenso. In cambio Lei ottiene:
        20–30 ore di shooting e marketing, accesso ai 4 portali principali, 3 social network,
        rete di colleghi, gestione completa di chiamate e visite, contratti pronti, supporto
        notarile. Se vendesse da solo a 250.000 (prezzo medio quando uno vende senza agenzia),
        perderebbe 30.000 euro. La mia provvigione le fa guadagnare 21.600 euro netti."</em>
      </p>
      <p>
        Hai trasformato un <strong>costo</strong> in un <strong>investimento</strong>. È
        psicologia base ma il 70% degli agenti non la usa.
      </p>

      <h2><Handshake size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />5. La penale dell'esclusiva la giri al contrario</h2>
      <p>
        Il proprietario teme l'esclusiva perché "non posso più dare incarico ad altri". Tu
        rispondi: <em>"Esatto. E sa perché? Perché se Lei dà l'incarico a 5 agenzie, le 5
        agenzie sanno che non lavoreranno a fondo: tanto un'altra venderà prima. Risultato: la
        Sua casa rimbalza tra 5 colonne tristi, perde freschezza, e dopo 3 mesi sembra una casa
        che nessuno vuole. Con l'esclusiva, io ho l'interesse a chiuderla in 30–60 giorni, e
        Lei ha un solo interlocutore."</em>
      </p>
      <p>
        Hai fatto capire che <strong>l'esclusiva conviene a lui, non a te</strong>. Adesso può
        firmare.
      </p>

      <h3>In sintesi</h3>
      <p>
        Mandati in esclusiva = <strong>ascolto + documentazione + metodo</strong>. Non sconti,
        non promesse, non parole. Se entri preparato (report, piano marketing, calcoli), e
        ascolti i primi 5 minuti, l'esclusiva la firmi al primo appuntamento nel 60% dei casi.
        Senza preparazione: ti sentirai dire "ci pensiamo" — e quel "ci pensiamo" diventa "no"
        nel 90% dei casi. Prepararsi paga.
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST 5 — Home staging low cost
// ─────────────────────────────────────────────────────────────────────────────
function HomeStagingLowCost() {
  return (
    <>
      <p className="lead">
        Il vero home staging non è "comprare mobili nuovi". È <strong>far percepire la casa
        più grande, più pulita, più desiderabile</strong> di quella che vede chi entra senza
        preparazione. E si fa in 2 ore, gratis, con quello che già c'è in casa. Ecco le 7 cose
        da fare prima di ogni visita o shooting.
      </p>

      <h2><Broom size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />1. Spersonalizza tutto</h2>
      <p>
        Foto di famiglia, calamite del frigo, magneti del Mar Rosso, calendari delle figlie:
        TUTTO via. Il cliente che entra deve immaginarsi <strong>la sua famiglia</strong> in
        quella casa, non vedere la storia della famiglia attuale. Riempi 2 scatoloni in
        camera/cantina e li nascondi. 15 minuti di lavoro, effetto enorme.
      </p>

      <h2><Sun size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />2. Apri tutto, accendi tutto</h2>
      <p>
        Tende aperte, persiane su, luci accese in <strong>ogni</strong> stanza, anche in pieno
        giorno. Una casa luminosa sembra più grande del 30%. Sostituisci eventuali lampadine
        gialle (3000K) con bianche calde (4000K) — costano 3 euro al supermercato. Differenza
        percepita: enorme.
      </p>

      <h2><Broom size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />3. Sgombra le superfici orizzontali</h2>
      <p>
        Tavoli, mensole, comodini, davanzali: il 70% degli oggetti via. Lascia 2-3 elementi
        per superficie (un libro, una pianta, una candela). Il resto in scatoloni. Una stanza
        con superfici sgombre sembra più grande, più ordinata, <strong>più costosa</strong>.
      </p>

      <h2><Crop size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />4. Nascondi i fili</h2>
      <p>
        TV, router, caricatori, ciabatte, prolunghe: nascondi o raggruppa con fascette. Un
        salone con 6 cavi neri sul muro sembra disordinato anche se è impeccabile. Costa 2
        euro di fascette e 10 minuti.
      </p>

      <h2><House size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />5. Bagno e cucina come un hotel</h2>
      <p>
        <strong>Bagno</strong>: spazzolini, schiume, asciugamani usati, prodotti igienici → in
        un mobile. Lascia 1 asciugamano pulito piegato + 1 candela + 1 saponetta nuova.
        Pavimento asciutto. Specchio senza schizzi. <strong>Cucina</strong>: piano cottura
        sgombero, lavandino vuoto, niente stracci a vista, niente bottiglie sul piano. Frigo
        senza calamite. Lavastoviglie chiusa.
      </p>

      <h2><Sparkle size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />6. Profuma (ma non come un negozio)</h2>
      <p>
        Mai diffusori chimici aggressivi (vaniglia, fiori sintetici): puzzano di "agenzia
        immobiliare che vuole nascondere qualcosa". Soluzioni gratis e perfette:
      </p>
      <ul>
        <li>Caffè appena fatto (lascia la moka su, non importa se non si beve)</li>
        <li>Forno acceso a 100° con scorza di limone + cannella + chiodi di garofano (15 minuti)</li>
        <li>Una finestra aperta 10 minuti prima della visita per arieggiare</li>
        <li>Sapone alla lavanda nei bagni (NON profumatori plug-in)</li>
      </ul>

      <h2><Camera size={22} weight="fill" className="inline -mt-1 mr-2 text-[#7C8B6E]" />7. Fiori freschi e piante (il dettaglio da rivista)</h2>
      <p>
        Un mazzo di fiori freschi sul tavolo della cucina (5 euro al supermercato) +
        una pianta verde nel salone trasformano la percezione. È il dettaglio che fa la
        differenza tra "casa in vendita" e "casa abitata da qualcuno che ci tiene". Costo:
        10 euro. Effetto: più 5–10% sulle offerte ricevute.
      </p>

      <h3>In sintesi</h3>
      <p>
        Home staging low cost = <strong>spersonalizza, illumina, sgombera, nascondi, profuma,
        decora</strong>. 2 ore di lavoro, 15 euro di spesa, +5–10% sulle offerte. È la cosa
        più ROI-positiva che un agente può proporre al proprietario prima di pubblicare.
        Insegnaglielo: te ne sarà grato per sempre.
      </p>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// POST 6 — Idealista vs Immobiliare.it
// ─────────────────────────────────────────────────────────────────────────────
function IdealistaVsImmobiliare() {
  return (
    <>
      <p className="lead">
        I due portali italiani principali pesano in modo diverso ogni elemento dell'annuncio.
        Pubblicare su entrambi va bene, ma <strong>l'ordine</strong> e <strong>l'ottimizzazione
        per ciascuno</strong> fanno la differenza tra prima pagina e pagina 4. Ecco la guida
        operativa 2026.
      </p>

      <h2>Le differenze chiave (nel motore di ranking)</h2>
      <p>
        <strong>Idealista</strong>: premia tantissimo le <em>foto</em> (qualità, numero, ordine).
        Penalizza annunci con meno di 8 foto. Considera molto la <em>completezza dei campi</em>
        (classe energetica, riscaldamento, esposizione, anno costruzione). Premia gli annunci
        nuovi per ~14 giorni, poi calano se non hai aggiornamenti.
      </p>
      <p>
        <strong>Immobiliare.it</strong>: premia il <em>prezzo competitivo</em> rispetto alla
        zona. Algoritmo più aggressivo sulla "freschezza" — l'annuncio scende rapidamente se
        non aggiorni. Pesa molto i <em>tag descrittivi</em> nel testo (parole chiave: "ristrutturato",
        "ascensore", "balcone"). Mostra prima gli annunci con planimetria caricata.
      </p>

      <h2>Strategia di pubblicazione: in che ordine</h2>
      <p>
        <strong>Pubblica prima su Idealista</strong>, poi 24 ore dopo su Immobiliare.it. Perché:
      </p>
      <ul>
        <li>Idealista ti dà 14 giorni di "freschezza" e una visibilità organica più forte all'inizio</li>
        <li>Immobiliare.it indicizza in 2 ore e ti dà una "spinta nuovo" indipendente</li>
        <li>Pubblicando in due tempi raddoppi l'effetto "annuncio appena uscito" su Google</li>
      </ul>
      <p>
        <strong>Non pubblicare su 8 portali contemporaneamente</strong>. Diluisci la spinta
        organica. Meglio 2 portali ottimizzati che 8 abbandonati.
      </p>

      <h2>Foto: regole specifiche per ciascun portale</h2>
      <p>
        <strong>Idealista</strong>: minimo 12 foto, prima foto = soggiorno principale (mai
        bagno, mai esterno). Ordine: soggiorno → cucina → camere → bagno → balcone/giardino →
        planimetria (sempre ultima). Risoluzione minima 1280px sul lato lungo.
      </p>
      <p>
        <strong>Immobiliare.it</strong>: minimo 10 foto, prima foto = <em>quella più
        rappresentativa</em> (può essere anche un'esterna in case con giardino o ville). Stesso
        ordine ma planimetria può andare anche al 3° posto se molto chiara. Risoluzione minimo
        1024px ma carica il massimo possibile.
      </p>

      <h2>Titolo: differenza di formato</h2>
      <ul>
        <li><strong>Idealista</strong>: 50–60 caratteri massimo. Formato "Tipologia, dettaglio, zona". Es. "Trilocale ristrutturato, ascensore, Brera"</li>
        <li><strong>Immobiliare.it</strong>: 70–80 caratteri massimo. Più spazio per dettagli. Es. "Trilocale 90mq ristrutturato, terrazzo, ascensore, MM Lanza Brera"</li>
      </ul>
      <p>
        Non copiare lo stesso titolo. Adatta a ciascun portale.
      </p>

      <h2>Descrizione: lunghezza e parole chiave</h2>
      <p>
        <strong>Idealista</strong>: 800–1200 caratteri. Stile narrativo. Le keyword pesano meno
        del titolo, ma le <em>caratteristiche tecniche compilate nei campi</em> (NON nel testo)
        pesano molto.
      </p>
      <p>
        <strong>Immobiliare.it</strong>: 1500–2500 caratteri. Le keyword nel testo pesano
        davvero. Inserisci 2-3 volte le parole chiave principali (zona, tipologia, "ristrutturato",
        ecc.). Senza esagerare: keyword stuffing penalizza.
      </p>

      <h2>Aggiornamento: quando rinfrescare</h2>
      <p>
        <strong>Idealista</strong>: non puoi "rinfrescare" l'annuncio facilmente. Se cala
        visibilità dopo 30 giorni, devi modificare prezzo o foto principale per riattivare
        l'algoritmo.
      </p>
      <p>
        <strong>Immobiliare.it</strong>: ogni 7-10 giorni cambia 1 elemento (foto principale,
        ordine foto, riga descrizione). L'algoritmo lo legge come "nuovo aggiornamento" e ti
        ridà spinta. Funziona davvero.
      </p>

      <h2>Errori da non fare mai</h2>
      <ul>
        <li><strong>Non pubblicare con prezzo "su richiesta"</strong>: entrambi i portali ti penalizzano (l'utente filtra per fascia di prezzo, sei invisibile)</li>
        <li><strong>Non lasciare campi vuoti</strong>: classe energetica "non disponibile" → annuncio scende nelle ricerche filtrate</li>
        <li><strong>Non duplicare lo stesso annuncio su sub-account</strong>: Idealista ti banna l'agenzia se lo scopre</li>
        <li><strong>Non usare le stesse foto a piena risoluzione</strong>: ridimensiona alle dimensioni richieste, l'algoritmo legge il file e premia chi rispetta i formati</li>
      </ul>

      <h3>In sintesi</h3>
      <p>
        Idealista pesa <strong>foto + completezza campi + freschezza prima 14 giorni</strong>.
        Immobiliare.it pesa <strong>prezzo competitivo + keyword nel testo + aggiornamento
        frequente</strong>. Pubblica prima su Idealista, 24 ore dopo su Immobiliare.it.
        Adatta titolo e descrizione a ciascuno. Aggiorna Immobiliare.it ogni 10 giorni. Tutto
        il resto è rumore.
      </p>
    </>
  );
}
